import { NavLink } from "react-router-dom";
import { paths } from "@/config/paths";

type PortalTabBarProps = {
  activeProjectId?: string;
  decisionCount: number;
  changeOrderCount: number;
};

type Tab = {
  key: string;
  label: string;
  hrefFor: (id: string) => string;
  badge?: boolean;
};

const TABS: Tab[] = [
  {
    key: "overview",
    label: "Overview",
    hrefFor: (id: string) => paths.app.portal.project.overview.getHref(id),
  },
  {
    key: "progress",
    label: "Progress",
    hrefFor: (id: string) => paths.app.portal.project.progress.getHref(id),
  },
  {
    key: "materials",
    label: "Materials",
    hrefFor: (id: string) => paths.app.portal.project.materials.getHref(id),
  },
  {
    key: "payments",
    label: "Payments",
    hrefFor: (id: string) => paths.app.portal.project.payments.getHref(id),
  },
  {
    key: "invoices",
    label: "Invoices",
    hrefFor: (_id: string) => paths.app.portal.invoices.getHref(),
  },
  {
    key: "decisions",
    label: "Decisions",
    hrefFor: (id: string) => paths.app.portal.project.decisions.getHref(id),
    badge: true,
  },
  {
    key: "change-orders",
    label: "Change Orders",
    hrefFor: (id: string) => paths.app.portal.project.changeOrders.getHref(id),
    badge: true,
  },
  {
    key: "punchlists",
    label: "Punch List",
    hrefFor: (id: string) => paths.app.portal.project.punchlists.getHref(id),
  },
];

const PortalTabBar = ({ activeProjectId, decisionCount, changeOrderCount }: PortalTabBarProps) => {
  const labels: Record<string, string> = {
    overview: "Overview",
    progress: "Progress",
    materials: "Materials",
    payments: "Payments",
    invoices: "Invoices",
    decisions: "Decisions",
    "change-orders": "Change Orders",
    punchlists: "Punch List",
  };

  return (
    <nav className="h-12 bg-(--color-bg-surface) border-b border-(--color-border-default) flex items-stretch px-2 md:px-4 shrink-0 overflow-x-auto">
      {TABS.map((tab) => {
        const href = activeProjectId ? tab.hrefFor(activeProjectId) : "#";
        const showBadge = tab.badge && (
          (tab.key === "decisions" && decisionCount > 0) ||
          (tab.key === "change-orders" && changeOrderCount > 0)
        );
        const badgeCount = tab.key === "decisions" ? decisionCount : changeOrderCount;
        return (
          <NavLink
            key={tab.key}
            to={href}
            end={tab.key === "overview"}
            className={({ isActive }) =>
              `relative flex items-center gap-1.5 px-3 md:px-4 text-sm font-medium whitespace-nowrap transition-colors ${
                isActive
                  ? "text-(--color-info-700) after:absolute after:bottom-0 after:inset-x-2 after:h-0.5 after:rounded-full after:bg-(--color-info-600)"
                  : "text-(--color-text-secondary) hover:text-(--color-text-primary)"
              }`
            }
          >
            <span>{labels[tab.key]}</span>
            {showBadge && (
              <span className="inline-flex items-center justify-center rounded-full bg-(--color-danger-500) text-white text-[11px] font-medium px-1.5 min-w-[18px] h-[18px]">
                {badgeCount > 99 ? "99+" : badgeCount}
              </span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
};

export default PortalTabBar;
